import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UpsertCareAuthorizationInput } from "@shared/validation/careAuthorization.schema";
import { PICKUP_CONSENT_ENABLED } from "@shared/constants/features";
import { apiClient, ApiError } from "./client";

export interface CareAuthorizationData {
  id: string;
  childId: string;
  consentToTreat: boolean;
  consentGrantedByName: string | null;
  /** Server-stamped when consent flips on; never sent by the client. */
  consentGrantedAt: string | null;
  insuranceCarrier: string | null;
  insurancePolicyNumber: string | null;
  insuranceGroupNumber: string | null;
  preferredHospital: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

const careAuthorizationKey = (childId: string) =>
  ["children", childId, "care-authorization"] as const;

export function useCareAuthorization(childId: string | undefined) {
  return useQuery<CareAuthorizationData | null, ApiError>({
    queryKey: childId
      ? careAuthorizationKey(childId)
      : ["children", "__none__", "care-authorization"],
    queryFn: () =>
      apiClient<CareAuthorizationData | null>(`/api/care-authorizations/${childId}`),
    // Off, the route is not mounted: never fire a request that would 404.
    enabled: PICKUP_CONSENT_ENABLED && Boolean(childId),
  });
}

export function useSaveCareAuthorization(childId: string) {
  const queryClient = useQueryClient();
  return useMutation<CareAuthorizationData, ApiError, UpsertCareAuthorizationInput>({
    mutationFn: (vars) =>
      apiClient<CareAuthorizationData>(`/api/care-authorizations/${childId}`, {
        method: "PUT",
        body: JSON.stringify(vars),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: careAuthorizationKey(childId) });
    },
  });
}
